import { storeToRefs } from 'pinia';
import { type Ref, computed } from 'vue';
import { getMatchLiveTimeDisplay } from '@/components/oddsDisplay/matchDisplay/useMatchDisplay';
import { computedInterval } from '@/composable/computedInterval';
import { i18n } from '@/core/lib/i18n';
import { getTranslated } from '@/core/lib/translationHelper';
import type { IEventLike } from '@/interface/IEventLike';
import type {
    IBetConstructLiveScore,
    IBetGeniusLiveScore,
    IBetRadarLiveScore,
    ILiveScoreDisplay,
    IProviderLiveScore,
    ISdtV2LiveScore,
    Score,
} from '@/interface/ILiveScore';
import { EnumLanguage, LiveScoreProvider, MatchStatus, SiteStyle, SportType } from '@/interface/enum';
import { useToggleStore } from '@/store/toggleStore';

type LiveScoreHandler = (event: IEventLike, liveScore: IProviderLiveScore, siteStyle: SiteStyle) => ILiveScoreDisplay | null

const liveScoreHandlerMapping: Partial<Record<LiveScoreProvider, LiveScoreHandler>> = {
    [LiveScoreProvider.Sdt]: getSdtLiveScoreDisplay,
    [LiveScoreProvider.BetConstruct]: getBetConstructLiveScoreDisplay,
    [LiveScoreProvider.BetRadar]: getBetRadarLiveScoreDisplay,
    [LiveScoreProvider.Cricket]: getCricketLiveScoreDisplay,
    [LiveScoreProvider.BetGenius]: getBetGeniusLiveScoreDisplay,
};

export function useLiveScoreDisplay(
    event: Ref<IEventLike | null>,
    providerLiveScore: Ref<IProviderLiveScore | undefined>,
    sboLiveScore: Ref<Score | null>,
    siteStyle: SiteStyle,
    isMainMarket?: Ref<boolean>,
) {
    const { liveScoreProviderConfig } = storeToRefs(useToggleStore());
    const supportedProviders = computed(() => (event.value ? liveScoreProviderConfig.value[event.value.sportType] ?? [] : []));

    const _sboScore = computed(() => (
        (isMainMarket?.value ?? false)
            ? (providerLiveScore.value?.sbo?.score ?? null)
            : sboLiveScore.value
    ));
    const sboScore = computedInterval(() => getSboLiveScoreDisplay(event.value, _sboScore.value), 20 * 1000);

    const displayScore = computed(() => {
        const currentEvent = event.value;
        if (!currentEvent) return null;

        const resolveResult = (provider: LiveScoreProvider) => {
            if (provider === LiveScoreProvider.Sbo) return sboScore.value;
            if (!providerLiveScore.value) return null;
            return liveScoreHandlerMapping[provider]?.(currentEvent, providerLiveScore.value, siteStyle) ?? null;
        };

        const providerResults = supportedProviders.value.map(provider => ({
            provider,
            result: resolveResult(provider),
        }));

        const scoreSource = providerResults.find(x => x.result !== null);
        if (!scoreSource?.result) return null;

        const isSboPeriodValid = (currentEvent.extraInfo?.period ?? 0) > 0;
        const externalPeriodText = providerResults
            .find(x => x.result?.periodText && (x.provider !== LiveScoreProvider.Sbo || isSboPeriodValid))
            ?.result?.periodText;

        return { ...scoreSource.result, periodText: externalPeriodText ?? scoreSource.result.periodText };
    });

    return {
        displayScore,
    };
}

function getSboLiveScoreDisplay(event: IEventLike | null, liveScore: Score | null): ILiveScoreDisplay | null {
    if (!event) return null;
    return {
        score: liveScore
            ? { home: liveScore.home, away: liveScore.away }
            : undefined,
        periodText: getMatchLiveTimeDisplay(event),
    };
}

function getCricketLiveScoreDisplay(_event: IEventLike, liveScore: IProviderLiveScore | undefined, _siteStyle: SiteStyle): ILiveScoreDisplay | null {
    if (!liveScore?.cricket) return null;
    return {
        score: {
            home: `${liveScore.cricket.score.home}/${liveScore.cricket.homeDismissals}`,
            away: `${liveScore.cricket.score.away}/${liveScore.cricket.awayDismissals}`,
        },
        cricketDisplayText: `${i18n.t('cricket_live_score_inn')}: ${liveScore.cricket.innings} | ${i18n.t('cricket_live_score_over')}: ${liveScore.cricket.over}.${liveScore.cricket.delivery}`,
    };
}

function getSdtLiveScoreDisplay(event: IEventLike, liveScore: IProviderLiveScore, siteStyle: SiteStyle): ILiveScoreDisplay | null {
    if (!liveScore.sdtV2) return null;
    return getThirdPartyLiveScoreDisplay(event, liveScore.sdtV2, siteStyle);
}

function getBetConstructLiveScoreDisplay(event: IEventLike, liveScore: IProviderLiveScore, siteStyle: SiteStyle): ILiveScoreDisplay | null {
    if (!liveScore.betConstruct) return null;
    return getThirdPartyLiveScoreDisplay(event, liveScore.betConstruct, siteStyle);
}

function getBetRadarLiveScoreDisplay(event: IEventLike, liveScore: IProviderLiveScore, siteStyle: SiteStyle): ILiveScoreDisplay | null {
    if (!liveScore.betRadar) return null;
    return getThirdPartyLiveScoreDisplay(event, liveScore.betRadar, siteStyle);
}

function getBetGeniusLiveScoreDisplay(event: IEventLike, liveScore: IProviderLiveScore, siteStyle: SiteStyle): ILiveScoreDisplay | null {
    if (!liveScore.betGenius) return null;
    return getThirdPartyLiveScoreDisplay(event, liveScore.betGenius, siteStyle);
}

function getThirdPartyLiveScoreDisplay(event: IEventLike, providerLiveScore: IBetConstructLiveScore | IBetRadarLiveScore | IBetGeniusLiveScore | ISdtV2LiveScore, siteStyle: SiteStyle): ILiveScoreDisplay | null {
    if (providerLiveScore.matchStatus === MatchStatus.NotStarted) return null;

    const {
        quarterSportList,
        periodSportList,
        inningSportList,
        setGameScoreSportList,
    } = storeToRefs(useToggleStore());
    const isQuarterSport = quarterSportList.value.includes(event.sportType);
    const isPeriodSport = periodSportList.value.includes(event.sportType);
    const isInningSport = inningSportList.value.includes(event.sportType);
    const isSetGameScoreSport = setGameScoreSportList.value.includes(event.sportType);

    if (isQuarterSport) {
        if (isInvalid(providerLiveScore.inPlayPeriod)) return null;
        const leagueName = getTranslated(event.league.name, EnumLanguage.EN);
        return {
            score: {
                home: providerLiveScore.totalScore.home,
                away: providerLiveScore.totalScore.away,
            },
            periodText: getInPlayQuarterInfo(leagueName, providerLiveScore.inPlayPeriod, event.sportType),
        };
    }

    if (isSetGameScoreSport) {
        return {
            score: {
                home: providerLiveScore.gameScore.home,
                away: providerLiveScore.gameScore.away,
            },
            setDisplayText: getSetDisplayText(providerLiveScore.setScore, siteStyle),
            setScore: {
                home: providerLiveScore.setScore.home,
                away: providerLiveScore.setScore.away,
            },
        };
    }

    if (isPeriodSport) {
        if (isInvalid(providerLiveScore.inPlayPeriod)) return null;

        return {
            score: {
                home: providerLiveScore.totalScore.home,
                away: providerLiveScore.totalScore.away,
            },
            periodText: getInPlayPeriodInfo(providerLiveScore.inPlayPeriod),
        };
    }

    if (isInningSport) {
        if (isInvalid(providerLiveScore.inPlayInning)) return null;

        return {
            score: {
                home: providerLiveScore.totalScore.home,
                away: providerLiveScore.totalScore.away,
            },
            periodText: getInPlayInningInfo(providerLiveScore.inPlayInning),
        };
    }

    return {
        score: {
            home: providerLiveScore.totalScore.home,
            away: providerLiveScore.totalScore.away,
        },
        periodText: isPenalty(providerLiveScore.inPlayPeriod) ? 'PEN' : getMatchLiveTimeDisplay(event),
    };
}

function getInPlayPeriodInfo(inPlayPeriod: string) {
    if (isOverTime(inPlayPeriod)) {
        return i18n.t('live_score_over_time');
    }

    if (isPenalty(inPlayPeriod)) {
        return 'PEN';
    }

    return i18n.t('live_score_period', { n: inPlayPeriod });
}

function getInPlayInningInfo(inPlayInning: string) {
    if (isOverTime(inPlayInning)) {
        return i18n.t('live_score_extra');
    }

    return i18n.t('live_score_inning', { n: inPlayInning });
}

function getInPlayQuarterInfo(leagueName: string, inPlayPeriod: string, sportType: SportType) {
    if (isOverTime(inPlayPeriod)) {
        return i18n.t('live_score_over_time');
    }

    if (sportType === SportType.Basketball && isNcaaLeague(leagueName)) {
        return inPlayPeriod === '1' ? i18n.t('live_score_first_half') : i18n.t('live_score_second_half');
    }

    return i18n.t('live_score_quarter', { n: inPlayPeriod });
}

function isOverTime(inPlayInfo: string) {
    return inPlayInfo === 'OT';
}

function isPenalty(inPlayInfo: string) {
    return inPlayInfo === 'PEN';
}

function isInvalid(inPlayInfo: string) {
    return !inPlayInfo || inPlayInfo === '0';
}

function isNcaaLeague(leagueName: string) {
    return leagueName.startsWith('NCAA');
}

function getSetDisplayText(setScore: Score, siteStyle: SiteStyle) {
    if (siteStyle === SiteStyle.Asia) {
        return i18n.t('live_score_set', {
            homeScore: setScore.home,
            awayScore: setScore.away,
        });
    }
    return i18n.t('live_score_set_count', {
        count: setScore.home + setScore.away + 1,
    });
}
